import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useBranding } from '../context/BrandingContext';

/**
 * Promotion banner that sits above the fixed global header.
 *
 * The banner height is intentionally dynamic on phones and tablets. Long
 * messages and CTA labels can wrap without overlapping the header because a
 * ResizeObserver publishes the rendered height through
 * `--ccuk-promo-banner-height` for the app-shell CSS to consume.
 */
function isWithinWindow(startsAt, endsAt, now) {
  if (startsAt) {
    const start = new Date(startsAt);

    if (!Number.isNaN(start.getTime()) && now < start) {
      return false;
    }
  }

  if (endsAt) {
    const end = new Date(endsAt);

    if (!Number.isNaN(end.getTime()) && now >= end) {
      return false;
    }
  }

  return true;
}

function isExternalUrl(value) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

export default function PromoBanner() {
  const { promoBanner, refreshPromoBanner } = useBranding();

  const [tick, setTick] = useState(0);
  const bannerRef = useRef(null);

  const now = new Date();

  const withinWindow = isWithinWindow(
    promoBanner?.starts_at,
    promoBanner?.ends_at,
    now,
  );

  const shouldShow = Boolean(
    promoBanner?.enabled
      && String(promoBanner?.message || '').trim()
      && withinWindow,
  );

  /**
   * Re-evaluate scheduled start/end windows once per minute without requiring
   * a page refresh.
   */
  useEffect(() => {
    if (!promoBanner?.starts_at && !promoBanner?.ends_at) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTick((value) => value + 1);
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [promoBanner?.starts_at, promoBanner?.ends_at]);

  /**
   * Refresh the active campaign periodically so newly published admin changes
   * appear without forcing visitors to reload.
   */
  useEffect(() => {
    if (!refreshPromoBanner) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshPromoBanner();
    }, 5 * 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshPromoBanner]);

  /**
   * Apply the shell class and publish the real rendered banner height before
   * paint. This prevents a one-frame overlap on iPhone Safari and installed
   * PWAs when a long banner wraps to multiple lines.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    const clearBannerState = () => {
      body.classList.remove('has-promo-banner');
      root.style.setProperty('--ccuk-promo-banner-height', '0px');
    };

    if (!shouldShow) {
      clearBannerState();
      return clearBannerState;
    }

    body.classList.add('has-promo-banner');

    const banner = bannerRef.current;

    if (!banner) {
      return clearBannerState;
    }

    let animationFrameId = null;

    const commitHeight = () => {
      const height = Math.max(
        0,
        Math.ceil(banner.getBoundingClientRect().height),
      );

      root.style.setProperty(
        '--ccuk-promo-banner-height',
        `${height}px`,
      );

      window.dispatchEvent(
        new CustomEvent('promo-banner:height-changed', {
          detail: { height },
        }),
      );
    };

    const publishHeight = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(commitHeight);
    };

    /**
     * Synchronous first measurement happens inside useLayoutEffect, before
     * the browser paints the newly visible fixed banner.
     */
    commitHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(publishHeight)
      : null;

    resizeObserver?.observe(banner);

    window.addEventListener('resize', publishHeight, {
      passive: true,
    });

    window.addEventListener('orientationchange', publishHeight, {
      passive: true,
    });

    window.visualViewport?.addEventListener('resize', publishHeight, {
      passive: true,
    });

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();

      window.removeEventListener('resize', publishHeight);
      window.removeEventListener('orientationchange', publishHeight);

      window.visualViewport?.removeEventListener(
        'resize',
        publishHeight,
      );

      clearBannerState();
    };
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  const linkUrl = String(promoBanner.link_url || '').trim();

  const buttonLabel = String(
    promoBanner.button_label || 'Learn more',
  ).trim();

  const hasCallToAction = Boolean(linkUrl && buttonLabel);
  const externalLink = isExternalUrl(linkUrl);

  return (
    <aside
      ref={bannerRef}
      className="ccuk-promo-banner fixed left-0 right-0 top-0 z-[110]"
      data-testid="promo-banner"
      data-tick={tick}
      aria-label="Promotion"
      aria-live="polite"
      aria-atomic="true"
      style={{
        backgroundColor: promoBanner.bg_color || '#002FA7',
        color: promoBanner.text_color || '#FFFFFF',
        animation: promoBanner.glow
          ? 'promoBannerGlow 2.4s ease-in-out infinite'
          : undefined,
      }}
    >
      <div
        className="
          ccuk-promo-banner__inner
          ccuk-promo-banner__inner--message-only
          mx-auto
          max-w-[1400px]
        "
      >
        {/*
          Mobile:
          Message and button remain stacked so the button cannot bleed
          outside the screen.

          Tablet and desktop:
          Message and button sit together as one centred group.
        */}
        <div
          className="
            flex
            w-full
            min-w-0
            flex-col
            items-center
            justify-center
            gap-[0.35rem]
            sm:flex-row
            sm:gap-3
          "
        >
          <p
            className="ccuk-promo-banner__message"
            data-testid="promo-banner-message"
          >
            {promoBanner.message}
          </p>

          {hasCallToAction && (
            <a
              href={linkUrl}
              target={externalLink ? '_blank' : '_self'}
              rel={externalLink ? 'noopener noreferrer' : undefined}
              className="ccuk-promo-banner__cta"
              style={{
                backgroundColor:
                  promoBanner.button_bg_color || '#FFFFFF',

                color:
                  promoBanner.button_text_color || '#002FA7',
              }}
              data-testid="promo-banner-cta"
            >
              {buttonLabel}
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}